import {
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  HeadingLevel,
  BorderStyle,
  VerticalAlign,
  ShadingType,
  PageBreak,
  IParagraphOptions,
  IRunOptions,
} from 'docx';

// 颜色常量
export const COLORS = {
  primary: '1890ff',
  success: '52c41a',
  warning: 'faad14',
  error: 'f5222d',
  excellent: '52c41a',
  good: '1890ff',
  medium: 'faad14',
  poor: 'f5222d',
  lightGray: 'f5f5f5',
  gray: 'e8e8e8',
  darkGray: '666666',
  border: 'd9d9d9'
};

// 创建标题段落
export function createHeading(
  text: string,
  level: any = HeadingLevel.HEADING_1,
  options?: {
    alignment?: any;
    spacing?: { before?: number; after?: number };
    color?: string;
  }
): Paragraph {
  const { alignment = AlignmentType.LEFT, spacing, color } = options || {};

  return new Paragraph({
    text,
    heading: level,
    alignment,
    spacing: spacing || { after: 200 },
    run: color ? { color } : undefined
  });
}

// 创建普通段落
export function createParagraph(
  text: string | TextRun[],
  options?: IParagraphOptions
): Paragraph {
  const children = typeof text === 'string'
    ? [new TextRun(text)]
    : text;

  return new Paragraph({
    children,
    spacing: { before: 120, after: 120 },
    ...options
  });
}

// 创建带样式的文本
export function createTextRun(
  text: string,
  options?: IRunOptions
): TextRun {
  return new TextRun({
    text,
    font: { name: '微软雅黑' },
    ...options
  });
}

// 创建信息表格
export function createInfoTable(
  data: { label: string; value: string }[],
  options?: {
    labelWidth?: number;
    valueWidth?: number;
    headerColor?: string;
  }
): Table {
  const { labelWidth = 3000, valueWidth = 6000, headerColor = COLORS.lightGray } = options || {};

  const rows = data.map(item =>
    new TableRow({
      children: [
        new TableCell({
          children: [createParagraph(item.label, { alignment: AlignmentType.RIGHT })],
          width: { size: labelWidth, type: WidthType.DXA },
          shading: { type: ShadingType.SOLID, color: headerColor },
          verticalAlign: VerticalAlign.CENTER
        }),
        new TableCell({
          children: [createParagraph(item.value)],
          width: { size: valueWidth, type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER
        })
      ]
    })
  );

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border }
    }
  });
}

// 创建评分表格
export function createScoreTable(
  dimensions: {
    name: string;
    score: number;
    maxScore: number;
    percentage: number;
  }[]
): Table {
  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [createParagraph('维度', { alignment: AlignmentType.CENTER })],
        shading: { type: ShadingType.SOLID, color: COLORS.primary },
        verticalAlign: VerticalAlign.CENTER
      }),
      new TableCell({
        children: [createParagraph('得分', { alignment: AlignmentType.CENTER })],
        shading: { type: ShadingType.SOLID, color: COLORS.primary },
        verticalAlign: VerticalAlign.CENTER
      }),
      new TableCell({
        children: [createParagraph('满分', { alignment: AlignmentType.CENTER })],
        shading: { type: ShadingType.SOLID, color: COLORS.primary },
        verticalAlign: VerticalAlign.CENTER
      }),
      new TableCell({
        children: [createParagraph('得分率', { alignment: AlignmentType.CENTER })],
        shading: { type: ShadingType.SOLID, color: COLORS.primary },
        verticalAlign: VerticalAlign.CENTER
      })
    ]
  });

  const dataRows = dimensions.map(dim => {
    const scoreColor = getScoreColor(dim.score, dim.maxScore);
    return new TableRow({
      children: [
        new TableCell({
          children: [createParagraph(dim.name, { alignment: AlignmentType.CENTER })],
          verticalAlign: VerticalAlign.CENTER
        }),
        new TableCell({
          children: [
            createParagraph(
              [createTextRun(dim.score.toFixed(1), { color: scoreColor, bold: true })],
              { alignment: AlignmentType.CENTER }
            )
          ],
          verticalAlign: VerticalAlign.CENTER
        }),
        new TableCell({
          children: [createParagraph(dim.maxScore.toString(), { alignment: AlignmentType.CENTER })],
          verticalAlign: VerticalAlign.CENTER
        }),
        new TableCell({
          children: [createProgressBar(dim.percentage)],
          verticalAlign: VerticalAlign.CENTER
        })
      ]
    });
  });

  return new Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border }
    }
  });
}

// 创建进度条（使用特殊字符模拟）
function createProgressBar(percentage: number): Paragraph {
  const filled = Math.round(percentage / 10);
  const empty = 10 - filled;
  const progressText = '█'.repeat(filled) + '░'.repeat(empty) + ` ${percentage.toFixed(1)}%`;

  return createParagraph(
    [createTextRun(progressText, {
      color: getScoreColor(percentage, 100),
      font: { name: 'Consolas' }
    })],
    { alignment: AlignmentType.CENTER }
  );
}

// 创建错误分析表格
export function createErrorTable(
  errors: Array<{
    type: string;
    text: string;
    suggestion: string;
    severity: string;
  }>
): Table {
  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [createParagraph('序号', { alignment: AlignmentType.CENTER })],
        width: { size: 1000, type: WidthType.DXA },
        shading: { type: ShadingType.SOLID, color: COLORS.lightGray }
      }),
      new TableCell({
        children: [createParagraph('类型', { alignment: AlignmentType.CENTER })],
        width: { size: 1500, type: WidthType.DXA },
        shading: { type: ShadingType.SOLID, color: COLORS.lightGray }
      }),
      new TableCell({
        children: [createParagraph('原文', { alignment: AlignmentType.CENTER })],
        width: { size: 3000, type: WidthType.DXA },
        shading: { type: ShadingType.SOLID, color: COLORS.lightGray }
      }),
      new TableCell({
        children: [createParagraph('建议', { alignment: AlignmentType.CENTER })],
        width: { size: 3000, type: WidthType.DXA },
        shading: { type: ShadingType.SOLID, color: COLORS.lightGray }
      }),
      new TableCell({
        children: [createParagraph('严重程度', { alignment: AlignmentType.CENTER })],
        width: { size: 1500, type: WidthType.DXA },
        shading: { type: ShadingType.SOLID, color: COLORS.lightGray }
      })
    ]
  });

  const dataRows = errors.slice(0, 10).map((error, index) =>
    new TableRow({
      children: [
        new TableCell({
          children: [createParagraph((index + 1).toString(), { alignment: AlignmentType.CENTER })]
        }),
        new TableCell({
          children: [createParagraph(getErrorTypeText(error.type), { alignment: AlignmentType.CENTER })]
        }),
        new TableCell({
          children: [createParagraph(
            [createTextRun(error.text, { color: COLORS.error })],
            { alignment: AlignmentType.LEFT }
          )]
        }),
        new TableCell({
          children: [createParagraph(
            [createTextRun(error.suggestion, { color: COLORS.success })],
            { alignment: AlignmentType.LEFT }
          )]
        }),
        new TableCell({
          children: [createParagraph(
            getSeverityText(error.severity),
            { alignment: AlignmentType.CENTER }
          )]
        })
      ]
    })
  );

  return new Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: COLORS.border }
    }
  });
}

// 创建带边框的文本框
export function createBoxedParagraph(
  content: string | TextRun[],
  options?: {
    backgroundColor?: string;
    borderColor?: string;
    padding?: number;
  }
): Table {
  const { backgroundColor = COLORS.lightGray, borderColor = COLORS.border } = options || {};

  const children = typeof content === 'string'
    ? [createParagraph(content)]
    : [createParagraph(content)];

  return new Table({
    rows: [
      new TableRow({
        children: [
          new TableCell({
            children,
            shading: { type: ShadingType.SOLID, color: backgroundColor },
            margins: {
              top: 200,
              bottom: 200,
              left: 200,
              right: 200
            }
          })
        ]
      })
    ],
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 2, color: borderColor },
      bottom: { style: BorderStyle.SINGLE, size: 2, color: borderColor },
      left: { style: BorderStyle.SINGLE, size: 2, color: borderColor },
      right: { style: BorderStyle.SINGLE, size: 2, color: borderColor }
    }
  });
}

// 获取分数对应的颜色
export function getScoreColor(score: number, maxScore: number): string {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 85) return COLORS.excellent;
  if (percentage >= 70) return COLORS.good;
  if (percentage >= 50) return COLORS.medium;
  return COLORS.poor;
}

// 获取错误类型文字
export function getErrorTypeText(type: string): string {
  const typeMap: { [key: string]: string } = {
    grammar: '语法',
    spelling: '拼写',
    vocabulary: '词汇',
    structure: '结构',
    punctuation: '标点'
  };
  return typeMap[type] || type;
}

// 获取严重程度文字
export function getSeverityText(severity: string): string {
  const severityMap: { [key: string]: string } = {
    high: '严重',
    medium: '中等',
    low: '轻微'
  };
  return severityMap[severity] || severity;
}

// 格式化日期
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

// 获取等级文字
export function getGradeText(score: number, isApplication: boolean): string {
  if (isApplication) {
    if (score >= 13) return '第五档（优秀）';
    if (score >= 10) return '第四档（良好）';
    if (score >= 7) return '第三档（及格）';
    if (score >= 4) return '第二档（较差）';
    return '第一档（差）';
  } else {
    if (score >= 22) return '第六档（优秀）';
    if (score >= 18) return '第五档（良好）';
    if (score >= 15) return '第四档（中等）';
    if (score >= 11) return '第三档（及格）';
    if (score >= 6) return '第二档（较差）';
    return '第一档（差）';
  }
}

// 创建分页符
export function createPageBreak(): Paragraph {
  return new Paragraph({
    children: [new PageBreak()]
  });
}